import { IOrganization, IOrganizationLicense } from 'types/models/OrganizationModels';
import { action, makeObservable, observable } from 'mobx';
import { INotificationsStore } from './NotificationsStore';
import { t } from 'i18next';
import { IOrganizationService } from 'services/organizationsService';
import { BaseStoreClass, IBaseStoreClass } from './BaseStoreClass';
import { getErrorMessage, storeResponseHandler } from 'utilities/ErrorHelper';

export interface IOrganizationsStore extends IBaseStoreClass {
  organizations: IOrganization[];
  selectedOrganization: IOrganization | null;

  isLoading: boolean;
  isFormLoading: boolean;

  getAllOrganizations: ({ pageNumber, take }: { pageNumber: number; take: number }) => Promise<void>;
  getSingleOrganization: (id: string) => Promise<any>;
  addOrganization: (organization: { name: string; shortName: string, licenses: string[] }) => Promise<void>;

  isSelectedOrganizationChanged: (orgChanges?: Partial<IOrganization>) => boolean;
  setSelectedOrganization: (organization: IOrganization | null) => void;
}

export class OrganizationsStore extends BaseStoreClass implements IOrganizationsStore {
  organizations: IOrganization[] = [];
  selectedOrganization: IOrganization | null = null;
  immutableSelectedOrganization: IOrganization | null = null;
  licenseTypes: IOrganizationLicense[] = [];

  // Loading indicators
  isLoading: boolean = false;
  isFormLoading: boolean = false;

  private _notificationsStore: INotificationsStore;
  private _organizationService: IOrganizationService;

  constructor(organizationService: IOrganizationService, notificationsStore: INotificationsStore) {
    super();

    this._notificationsStore = notificationsStore;
    this._organizationService = organizationService;

    makeObservable(this, {
      organizations: observable,
      selectedOrganization: observable,
      isLoading: observable,
      isFormLoading: observable,
      licenseTypes: observable,
      isSelectedOrganizationChanged: action,
      setSelectedOrganization: action
    });
  }

  getAllOrganizations = async ({ pageNumber, take }: { pageNumber: number; take: number }) => {
    try {
      this.setIsLoading(true);
      const response = await this._organizationService.getAll({
        page: pageNumber,
        count: take,
        filters: this.filtersQueryStr(this.filters),
      });

      if (response.data.organizations && !response.isError) {
        this.setOrganizations(response.data.organizations);
      }
    } catch (error: any) {
      this._notificationsStore.addToast('Error', getErrorMessage(error), 'error');
    } finally {
      this.setIsLoading(false);
    }
  };

  getSingleOrganization = async (id: string) => {
    try {
      this.setIsLoading(true);
      const response = await this._organizationService.getOne(id);
      return response;
    } catch (error: any) {
      this._notificationsStore.addToast('Error', getErrorMessage(error), 'error');
    } finally {
      this.setIsLoading(false);
    }
  };

  public addOrganization = async (organization: { name: string; shortName: string, licenses: string[] }, onSuccess?: () => void) => {
    try {
      this.setIsFormLoading(true);
      const response = await this._organizationService.addOne(organization as IOrganization);
      storeResponseHandler(response, t('organizationAddedSuccessfully'), this._notificationsStore.addToast);

      if (response.data && !response.isError) {
        this.updateOrganizations(response.data);
        if (onSuccess) {
          onSuccess();
        }
      }
    } catch (error: any) {
      this._notificationsStore.addToast('Error', getErrorMessage(error), 'error');
    } finally {
      this.setIsFormLoading(false);
    }
  };

  public updateOrganization = async (organization: IOrganization, CB?: () => void) => {
    try {
      this.setIsFormLoading(true);
      const response = await this._organizationService.patchOne(organization);

      storeResponseHandler(response, t('organizationUpdatedSuccessfully'), this._notificationsStore.addToast);

      if (response) {
        this.updateOrganizations(organization);
        if (CB) CB();
      }
    } catch (error: any) {
      this._notificationsStore.addToast('Error', getErrorMessage(error), 'error');
    } finally {
      this.setIsFormLoading(false);
    }
  };


  public async getAllLicenseTypes() {
    try {
      this.setIsLoading(true);
      const response = await this._organizationService.getLicenseTypes();
      this.licenseTypes = response.licenseTypeToFeatureMappings;

      return response;
    } catch (error: any) {
      this._notificationsStore.addToast('Error', getErrorMessage(error), 'error');
    } finally {
      this.setIsLoading(false);
    }
    return [];
  }

  // ==================================UTILS==================================
  setSelectedOrganization(organization: IOrganization | null) {
    this.selectedOrganization = organization;
    this.immutableSelectedOrganization = organization;
  }

  isSelectedOrganizationChanged(orgChanges: Partial<IOrganization> = {}) {
    return JSON.stringify({ ...this.selectedOrganization, ...orgChanges }) !== JSON.stringify(this.immutableSelectedOrganization);
  }

  setIsLoading(status: boolean) {
    this.isLoading = status;
  }

  setIsFormLoading(status: boolean) {
    this.isFormLoading = status;
  }

  setOrganizations(organizations: IOrganization[]) {
    this.organizations = organizations;
  }

  updateOrganizations(organization: IOrganization) {
    const index = this.organizations.findIndex((org) => org.id === organization.id);

    if (index > -1) {
      this.organizations = [...this.organizations.slice(0, index), organization, ...this.organizations.slice(index + 1)];
    }

    // if it doesn't exist, add it
    if (index === -1) {
      this.organizations = [...this.organizations, organization];
    }
  }
}
